import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { ChatKind } from "@/api";
import { ChatWithBotButton } from "@/components/chat/ChatWithBotButton";
import { LoginPanel } from "@/components/doc/left-sidebar/LoginPanel";
import { Menu } from "@/components/doc/right-sidebar/Menu";
import { RequestPermissionPanel } from "@/components/request-permission/RequestPermissionPanel";
import { Sidebar } from "@/components/sidebar";
import { SidebarToolbar } from "@/components/sidebar/SidebarToolbar";
import { SidebarVisibilityToggle } from "@/components/sidebar/SidebarVisibilityToggle";
import { getTaylorSeriesSelection } from "@/input/utils/taylorSeriesAction";
import { useStore } from "@/store";
import { useChats } from "@/store/useChats";
import { trackEvent, type EventMap } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";
import { useShowPublicProfile } from "@/utils/useShowPublicProfile";

import { getGroupObjects } from "./node-viewer/utils";
import { NodeProps } from "./NodeProps";
import { TaylorSeriesPanel } from "./taylor-series/TaylorSeriesPanel";
import { TopPanel } from "./TopPanel";

import type { DocumentModel } from "@/models/DocumentModel";

type RightPanelSource = EventMap["right_panel_clicked"]["source"];

const handlePanelClick = (e: React.MouseEvent) => {
  const panel = (e.target as HTMLElement).closest("[data-panel]");
  if (panel) {
    const source = panel.getAttribute("data-panel") as RightPanelSource;
    if (source) {
      trackEvent("right_panel_clicked", { source });
    }
  }
};

const RightSidebarContent = observer(({ doc }: { doc?: DocumentModel }) => {
  const { auth, view } = useStore();
  const chats = useChats();
  const showPublicProfile = useShowPublicProfile();
  const t = useTranslations("sidebar");

  if (view.rightSidebarState === "hidden" || !doc) return null;

  const showEditPanels = doc.editable;
  const taylorSelection = getTaylorSeriesSelection(doc);

  return (
    <div
      className={twMerge(
        "flex flex-col flex-1 h-full pointer-events-none! *:pointer-events-auto",
        view.isRightSidebarCollapsed &&
          "relative overflow-y-auto no-scrollbar -m-2.5 -ml-9 p-2.5 pl-9",
      )}
    >
      {!view.isRightSidebarCollapsed && (
        <>
          {!auth.isLoggedIn && (
            <Sidebar.Group>
              <LoginPanel />
            </Sidebar.Group>
          )}
          {auth.isLoggedIn && !doc.locked && (!doc.editable || showPublicProfile) && (
            <Sidebar.Group>
              <RequestPermissionPanel />
            </Sidebar.Group>
          )}
        </>
      )}
      {showEditPanels && (
        <Sidebar.Group className="p-0" shrink={!view.isRightSidebarCollapsed}>
          <Sidebar.Scrollable
            className={twMerge("p-1.25", view.isRightSidebarCollapsed && "flex flex-col gap-1")}
            onClick={handlePanelClick}
          >
            {view.isRightSidebarCollapsed ? (
              // One Tab stop for the whole column of panel buttons
              <SidebarToolbar vertical label={t("toolbars.formatting")} className="gap-1">
                <TopPanel doc={doc} />
                <NodeProps doc={doc} isCollapsed />
              </SidebarToolbar>
            ) : (
              <>
                <TopPanel doc={doc} />
                {taylorSelection.type === "ready" && (
                  <TaylorSeriesPanel doc={doc} selection={taylorSelection} />
                )}
                <NodeProps doc={doc} />
              </>
            )}
          </Sidebar.Scrollable>
        </Sidebar.Group>
      )}
      {/* Gate on objects existing: an empty Sidebar.Group paints a stray card. */}
      {!showEditPanels && getGroupObjects(doc).length > 0 && (
        <Sidebar.Group className="p-0" shrink={!view.isRightSidebarCollapsed}>
          <Sidebar.Scrollable
            className={twMerge("p-1.25", view.isRightSidebarCollapsed && "flex flex-col gap-1")}
            onClick={handlePanelClick}
          >
            <NodeProps doc={doc} isCollapsed={view.isRightSidebarCollapsed} />
          </Sidebar.Scrollable>
        </Sidebar.Group>
      )}
      <div className="mt-auto">
        {(view.isFloatingChatInputHidden ||
          (chats.isOpenChat && chats.activeChatKind === ChatKind.collective)) && (
          <ChatWithBotButton isCollapsed={view.isRightSidebarCollapsed} />
        )}
        <Sidebar.Group>
          <Menu doc={doc} />
        </Sidebar.Group>
      </div>
    </div>
  );
});

export const RightSidebar = observer(function RightSidebar({ doc }: { doc?: DocumentModel }) {
  const {
    view: { rightSidebarState, isRightSidebarCollapsed, isSidebarLayoutReady },
  } = useStore();
  const t = useTranslations("sidebar");

  // Not the "hidden" state, which is user-initiated: without a doc there is nothing to show.
  if (!doc) return null;

  return (
    <Sidebar.Root
      aria-label={t("label")}
      isHidden={rightSidebarState === "hidden"}
      side="right"
      className={twMerge(
        // Inset focus rings: the scrolling panels clip outlines drawn outside a control
        "print:hidden sidebar-focus-ring",
        isRightSidebarCollapsed && "w-[54px] min-w-[54px] pl-0",
        !isSidebarLayoutReady && "invisible",
      )}
    >
      {rightSidebarState !== "hidden" && (
        <SidebarVisibilityToggle
          position="right"
          className={twMerge(
            "sm:hidden",
            "absolute z-50 top-5",
            rightSidebarState === "full" ? "-left-[21px]" : "-left-[31px]",
          )}
          onToggle={() => doc.focus()}
        />
      )}
      <RightSidebarContent doc={doc} />
    </Sidebar.Root>
  );
});
